// CalendarGrid — shared Monday-first month grid. Pure presentational: it renders
// coloured event chips per day, a today marker and a "+N more" overflow. Reused by
// the project Calendar tab, the global calendar and the portal calendar.
import { ChevronLeft, ChevronRight, Users, Truck, CalendarCheck, UsersRound, Bell, CheckSquare } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
    "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
export const isoOf = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
export const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CalendarGrid({ year, month, eventsByDay, selectedDate, onSelectDay, onPrev, onNext, onToday, canQuickCreate = false, onQuickCreate }) {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday-first
    const grid = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

    return (
        <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5" data-testid="calendar-grid">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#1F1F1F]">{MONTHS[month - 1]} {year}</h3>
                <div className="flex items-center gap-1">
                    <button data-testid="calendar-prev-month" onClick={onPrev}
                        className="p-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F5F3F0] text-[#6F6A64]"><ChevronLeft size={16} /></button>
                    <button data-testid="calendar-today" onClick={onToday}
                        className="px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F5F3F0] text-[12px] font-semibold text-[#6F6A64]">Today</button>
                    <button data-testid="calendar-next-month" onClick={onNext}
                        className="p-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F5F3F0] text-[#6F6A64]"><ChevronRight size={16} /></button>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-wider text-[#9A968F] font-bold">
                {WEEKDAYS.map((w) => <div key={w} className="text-center py-1">{w}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
                {grid.map((day, idx) => {
                    if (!day) return <div key={`pad-${idx}`} />;
                    const dateStr = isoOf(year, month, day);
                    const dv = eventsByDay[dateStr] || [];
                    const badges = dv.filter((e) => e.source_module === "attendance" || e.source_module === "equipment");
                    const chips = dv.filter((e) => e.source_module !== "attendance" && e.source_module !== "equipment");
                    const isSelected = selectedDate === dateStr;
                    const isToday = dateStr === todayIso();
                    const isBlank = dv.length === 0;
                    const dayButton = (
                        <button key={isBlank && canQuickCreate ? undefined : day} data-testid={`calendar-day-${day}`} onClick={() => onSelectDay(dateStr)}
                            className={`min-h-[78px] rounded-lg border p-1.5 text-left transition ${isSelected ? "ring-2 ring-[var(--brand-accent,_#C75A1B)]" : "hover:bg-[#F5F3F0]"}`}
                            style={{ borderColor: isToday ? "var(--brand-accent, #C75A1B)" : "#F0EDE8", background: "#fff" }}>
                            <div className="flex items-center justify-between gap-1">
                                <div className="text-[11px] font-bold" style={{ color: isToday ? "var(--brand-accent, #C75A1B)" : "#6F6A64" }}>{day}</div>
                                {badges.length > 0 && (
                                    <div className="flex items-center gap-0.5" data-testid={`calendar-day-badges-${day}`}>
                                        {badges.map((b) => (
                                            <span key={b.id} title={b.title}
                                                className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                                                style={{ background: `${b.colour}1F`, color: b.colour }}>
                                                {b.source_module === "attendance" ? <Users size={10} /> : <Truck size={10} />}
                                                {b.count ?? ""}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="space-y-1 mt-1">
                                {chips.slice(0, 3).map((e) => (
                                    <div key={e.id} title={`${e.title} · ${e.event_type}`}
                                        className="text-[9.5px] truncate rounded px-1 py-0.5 text-white font-semibold"
                                        style={{ background: e.colour }}>{e.time ? `${e.time} ` : ""}{e.title}</div>
                                ))}
                                {chips.length > 3 && <div className="text-[9px] text-[#9A968F]">+{chips.length - 3} more</div>}
                            </div>
                        </button>
                    );
                    if (isBlank && canQuickCreate && onQuickCreate) {
                        return (
                            <DropdownMenu key={day}>
                                <DropdownMenuTrigger asChild>{dayButton}</DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-44">
                                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-[#9A968F]">Create new</DropdownMenuLabel>
                                    <DropdownMenuItem data-testid={`quick-create-visit-${dateStr}`} onClick={() => onQuickCreate("visit", dateStr)}>
                                        <CalendarCheck className="mr-2 h-3.5 w-3.5" /> Site visit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid={`quick-create-meeting-${dateStr}`} onClick={() => onQuickCreate("meeting", dateStr)}>
                                        <UsersRound className="mr-2 h-3.5 w-3.5" /> Meeting
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid={`quick-create-reminder-${dateStr}`} onClick={() => onQuickCreate("reminder", dateStr)}>
                                        <Bell className="mr-2 h-3.5 w-3.5" /> Reminder
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid={`quick-create-action-${dateStr}`} onClick={() => onQuickCreate("action", dateStr)}>
                                        <CheckSquare className="mr-2 h-3.5 w-3.5" /> Action
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        );
                    }
                    return dayButton;
                })}
            </div>
        </div>
    );
}
